import React, { useContext, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, Download, FileSpreadsheet, Loader2, Sparkles } from "lucide-react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import SegmentedToggle from "../ui/segmented-toggle";
import llmApi from "../../apis/llmApi";
import { readSpreadsheetFile } from "../../utils/spreadsheetReader";
import {
  buildSessionPlan,
  defaultStart,
  filterForRole,
  groupSessions,
  importedProgrammePayload,
  parseDay,
  readSheetRows,
  rolesIn,
  templateCsv,
} from "../../utils/programmeImport";
import { sheetText } from "../../apis/llm/programmeImportPrompt";
import { parseLocalDate, toIsoDate } from "../../utils/programmeSchedule";
import { DAY_KIND_LABELS } from "../../config/programmePlanner";
import { cn } from "../../lib/utils";

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const capitalise = (s) => String(s).charAt(0).toUpperCase() + String(s).slice(1);

const formatDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

// One step of a session as a short line: "3 × 8 Deadlift · 50 kg".
const stepLines = (plan) => {
  const out = [];
  const walk = (node, sets) => {
    if (!node) return;
    if (Array.isArray(node.children)) {
      if (node.unit === "rounds" && node.children.length && node !== plan) out.push({ heading: node.label });
      const n = node.unit === "sets" ? Number(node.value) || 1 : sets;
      node.children.forEach((c) => walk(c, n));
      return;
    }
    if (node.type === "Rest" || node.type === "Transition") return;
    const amount =
      node.unit === "s"
        ? node.value >= 60 && node.value % 60 === 0
          ? `${node.value / 60} min`
          : `${node.value} s`
        : node.unit === "cal"
          ? `${node.value} cal`
          : `${node.setScheme?.value ? node.setScheme.value.join("/") : node.value}`;
    const load = node.setScheme?.weight ? `${node.setScheme.weight.join("/")} kg` : node.weight ? `${node.weight} kg` : null;
    out.push({ text: `${sets > 1 ? `${sets} × ` : ""}${amount} ${node.label}`, load });
  };
  walk(plan, 1);
  return out;
};

const SessionRow = ({ session, onToggleExtra }) => {
  const [open, setOpen] = useState(false);
  const plan = useMemo(() => (open ? buildSessionPlan(session.name, session.rows) : null), [open, session]);
  const steps = session.rows.filter((r) => !/check-?in/i.test(`${r.part} ${r.exercise}`)).length;
  return (
    <div className={cn("rounded-xl border bg-card", session.extra ? "border-border/30 opacity-70" : "border-border/60")}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <span className="w-9 shrink-0 text-xs font-semibold text-muted-foreground">
            {session.day != null ? DAY_SHORT[session.day] : "—"}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-white">{session.name}</span>
            <span className="block text-[11px] text-muted-foreground">
              {session.extra
                ? "Shown as a note on the day"
                : `${steps} ${steps === 1 ? "step" : "steps"}${session.kind ? ` · ${DAY_KIND_LABELS[session.kind] || session.kind}` : ""}`}
            </span>
          </span>
          <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>
        <button
          type="button"
          onClick={onToggleExtra}
          aria-pressed={!session.extra}
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
            session.extra ? "border-border/50 text-muted-foreground" : "border-primary/50 bg-primary/15 text-primary",
          )}
        >
          {session.extra ? "Note" : "Session"}
        </button>
      </div>
      {open && (
        <ul className="grid gap-1 border-t border-border/40 px-3 py-2 text-xs">
          {stepLines(plan).map((s, i) =>
            s.heading ? (
              <li key={i} className="pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {s.heading}
              </li>
            ) : (
              <li key={i} className="flex gap-2 text-foreground/90">
                <span className="min-w-0 flex-1">{s.text}</span>
                {s.load && <span className="shrink-0 text-muted-foreground">{s.load}</span>}
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
};

/**
 * Import a programme someone already has (a coach's spreadsheet, or text
 * copied from a PDF) as a fixed programme: pick the file, check every week,
 * pick the week you're up to, save. Sheets in Khronos's template or a
 * coach's usual table layout are read on the device; anything else is read
 * by AI into the same rows, a call per sheet.
 */
const ProgrammeImportView = ({ onSave, onCancel }) => {
  const { llmSettings } = useContext(WorkoutContext);
  const fileRef = useRef(null);
  const [stage, setStage] = useState("pick");
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [title, setTitle] = useState("");
  const [sheets, setSheets] = useState([]);
  const [read, setRead] = useState([]); // [{ name, week, date, rows }]
  const [aiProgress, setAiProgress] = useState(null);
  const [pasted, setPasted] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [role, setRole] = useState("");
  const [extras, setExtras] = useState({}); // "week|key" → extra, as changed
  const [start, setStart] = useState(null);
  const [saving, setSaving] = useState(false);

  const today = toIsoDate(parseLocalDate(new Date()));
  const rows = useMemo(() => read.flatMap((s) => s.rows), [read]);
  const roles = useMemo(() => rolesIn(rows), [rows]);
  const weeks = useMemo(() => {
    const grouped = groupSessions(filterForRole(rows, role || null));
    return grouped.map((w) => ({
      ...w,
      sessions: w.sessions.map((s) => {
        const key = `${w.week}|${s.key}`;
        return key in extras ? { ...s, extra: extras[key] } : s;
      }),
    }));
  }, [rows, role, extras]);
  const unread = sheets.filter((s) => !read.some((r) => r.name === s.name) && s.rows.some((r) => r.filter(Boolean).length >= 3));

  const begin = (found, name) => {
    const dated = found.map((s) => ({ week: s.week, date: s.date }));
    const first = Math.min(...found.flatMap((s) => (s.week ? [s.week] : [1])));
    const last = Math.max(...found.flatMap((s) => (s.week ? [s.week] : [1])), first);
    const guess = defaultStart(dated, today);
    setStart({ ...guess, startWeek: Math.min(Math.max(guess.startWeek, first), last) });
    setTitle((t) => t || name);
    setStage("review");
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setStage("reading");
    try {
      const found = await readSpreadsheetFile(file);
      const name = file.name.replace(/\.(xlsx|csv)$/i, "");
      setFileName(file.name);
      setSheets(found);
      const ok = found
        .map((s) => ({ name: s.name, ...(readSheetRows(s) || {}) }))
        .filter((s) => s.rows?.length);
      setRead(ok);
      if (ok.length) begin(ok, name);
      else {
        setTitle(name);
        setStage("ai");
      }
    } catch (e) {
      setError(e.message || "Couldn't read that file.");
      setStage("pick");
    }
  };

  // AI reads each sheet (or the pasted text) into rows, one call each.
  const readWithAi = async (texts) => {
    setError(null);
    setAiProgress({ done: 0, total: texts.length });
    const out = [];
    try {
      for (let i = 0; i < texts.length; i++) {
        const { name, text, week } = texts[i];
        const result = await llmApi.readProgrammeText(text, llmSettings);
        if (result.title) setTitle((t) => t || result.title);
        const rowsRead = result.rows.map((r) => ({ ...r, week: r.week || week || 1, day: parseDay(r.day) }));
        out.push({ name, week: week || null, date: null, rows: rowsRead });
        setAiProgress({ done: i + 1, total: texts.length });
      }
      const all = [...read, ...out];
      setRead(all);
      setAiProgress(null);
      if (all.some((s) => s.rows.length)) begin(all, title || fileName.replace(/\.(xlsx|csv)$/i, "") || "Imported programme");
      else setError("No exercises found. Try another sheet, or paste one week at a time.");
    } catch (e) {
      setAiProgress(null);
      setError(e.message || "AI couldn't read it.");
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([templateCsv()], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "khronos-programme-template.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(
        importedProgrammePayload({
          title,
          weeks,
          startWeek: start.startWeek,
          weekOneMonday: start.weekOneMonday,
          source: { file: fileName || null },
        }),
      );
    } catch (e) {
      setSaving(false);
      setError(e.message || "Couldn't save the programme.");
    }
  };

  const mainCount = weeks.length ? Math.max(...weeks.map((w) => w.sessions.filter((s) => !s.extra).length)) : 0;
  const weekNumbers = weeks.map((w) => w.week);
  const startMonday = start
    ? (() => {
        const d = new Date(`${start.weekOneMonday}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + 7 * (start.startWeek - (weekNumbers[0] || 1)));
        return d.toISOString().slice(0, 10);
      })()
    : null;

  // In the page, not pinned: on a phone the tab bar covers the bottom edge.
  // Once in the summary card and again after the weeks.
  const saveBlock = (
    <div className="grid gap-2">
      {error && stage === "review" && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button type="button" className="h-12 w-full text-base font-bold" disabled={saving || !mainCount || !title.trim()} onClick={save}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Save programme
      </Button>
    </div>
  );

  return (
    <div className="mx-auto grid max-w-2xl grid-cols-[minmax(0,1fr)] gap-5 pb-8 pt-4 md:pt-8">
      <div>
        <button
          type="button"
          onClick={onCancel}
          className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <h2 className="text-[22px] font-bold text-white">Import a programme</h2>
        <p className="mt-1 max-w-[52ch] text-[13px] leading-relaxed text-muted-foreground">
          Bring in a programme you already have, like a coach's spreadsheet. Khronos runs it exactly as written: no
          AI changes to your sessions.
        </p>
      </div>

      {error && stage !== "review" && (
        <p role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {(stage === "pick" || stage === "reading") && (
        <div className="grid gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button type="button" className="h-12 text-base font-bold" disabled={stage === "reading"} onClick={() => fileRef.current?.click()}>
            {stage === "reading" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-5 w-5" />}
            {stage === "reading" ? "Reading…" : "Choose a spreadsheet"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Excel (.xlsx) or CSV. It's read on your device; only the weeks you save are stored.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="mr-1.5 h-4 w-4" /> Khronos template
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowPaste((v) => !v)}>
              <Sparkles className="mr-1.5 h-4 w-4" /> Paste text (from a PDF)
            </Button>
          </div>
          {showPaste && (
            <div className="grid gap-2">
              <Textarea
                rows={8}
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder="Paste the programme here, a week or a block at a time."
              />
              <Button
                type="button"
                disabled={!pasted.trim() || !!aiProgress}
                onClick={() => readWithAi([{ name: "Pasted", text: pasted.slice(0, 40000) }])}
              >
                {aiProgress ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Read with AI
              </Button>
            </div>
          )}
        </div>
      )}

      {stage === "ai" && (
        <div className="grid gap-3 rounded-2xl border border-border/60 bg-card p-4">
          <p className="text-sm text-white">
            This file isn't laid out in a way Khronos reads by itself. AI can read it: one request per tab
            {unread.length > 1 ? ` (${unread.length} tabs)` : ""}, using your AI settings.
          </p>
          <Button
            type="button"
            disabled={!!aiProgress}
            onClick={() =>
              readWithAi(unread.map((s) => ({ name: s.name, text: sheetText(s).text, week: readSheetRows(s)?.week || null })))
            }
          >
            {aiProgress ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {aiProgress ? `Reading ${aiProgress.done + 1} of ${aiProgress.total}…` : "Read it with AI"}
          </Button>
        </div>
      )}

      {stage === "review" && start && (
        <>
          <div className="grid min-w-0 gap-4 rounded-2xl border border-border/60 bg-card p-4">
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Name</span>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} />
            </label>
            <p className="text-sm text-white">
              {weeks.length} weeks · {mainCount} sessions a week
              {fileName && <span className="block truncate text-xs text-muted-foreground">From {fileName}</span>}
            </p>
            {roles.length > 0 && (
              <div className="grid gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground">Your role</span>
                <SegmentedToggle
                  value={role || "all"}
                  onChange={(v) => setRole(v === "all" ? "" : v)}
                  options={[{ value: "all", label: "Standard" }, ...roles.map((r) => ({ value: r, label: capitalise(r) }))]}
                />
                <p className="text-[11px] text-muted-foreground">Some sessions have extra exercises for these roles.</p>
              </div>
            )}
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Start at</span>
              <select
                value={start.startWeek}
                onChange={(e) => setStart((s) => ({ ...s, startWeek: Number(e.target.value) }))}
                className="h-10 rounded-md border border-border bg-background px-3 text-sm text-white"
              >
                {weekNumbers.map((w) => (
                  <option key={w} value={w}>
                    Week {w}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-muted-foreground">
                Week {start.startWeek} starts {formatDay(startMonday)}.
                {start.startWeek > weekNumbers[0] ? " Earlier weeks are kept but skipped." : ""}
              </span>
            </label>
            {saveBlock}
            {unread.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {unread.length} other {unread.length === 1 ? "tab wasn't" : "tabs weren't"} read (guides, logs and the
                like).
              </p>
            )}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
            {weeks.map((w) => (
              <section key={w.week} className={cn("grid grid-cols-[minmax(0,1fr)] gap-2", w.week < start.startWeek && "opacity-50")}>
                <h3 className="text-sm font-semibold text-white">
                  Week {w.week}
                  {w.week < start.startWeek && <span className="ml-2 text-xs font-normal text-muted-foreground">skipped</span>}
                </h3>
                {w.sessions.map((s) => (
                  <SessionRow
                    key={s.key}
                    session={s}
                    onToggleExtra={() => setExtras((x) => ({ ...x, [`${w.week}|${s.key}`]: !s.extra }))}
                  />
                ))}
              </section>
            ))}
          </div>

          {saveBlock}
        </>
      )}
    </div>
  );
};

export default ProgrammeImportView;
